import React, { useMemo, useEffect } from "react";
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { format as d3Format } from "d3";
import { PandasDataFrameProto } from "build-ts/dataframe";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { getPandasDtypeCategory, PandasDtypeCategory, parsePandasDataJson } from "components/analyse/data_table/dataTableUtils";

interface FormattedPandasTableProps {
  df?: PandasDataFrameProto;
  setNumberOfRows?: React.Dispatch<React.SetStateAction<number>>;
}

/** True when the column holds numeric values (right-align, apply numeric format). */
function isNumericDtype(dtype?: string): boolean {
  return getPandasDtypeCategory(dtype) === PandasDtypeCategory.NUMERIC;
}

/**
 * True when the column holds datetime values.
 * pandas to_json() serialises these as epoch milliseconds.
 */
function isDatetimeDtype(dtype?: string): boolean {
  return getPandasDtypeCategory(dtype) === PandasDtypeCategory.DATETIME;
}

/**
 * True when a cell value is a valid numeric primitive for a given dtype.
 * Combines the dtype check with a runtime safety guard (not NaN, actually a number).
 * Use this instead of bare `typeof value === "number"` throughout the table.
 */
function isNumericCellValue(value: any, dtype?: string): boolean {
  return isNumericDtype(dtype) && typeof value === "number" && !isNaN(value);
}

/**
 * True when a cell value is a valid epoch-ms primitive for a given dtype.
 * Use this before passing to epochConverterWithTimeZone.
 */
function isDatetimeCellValue(value: any, dtype?: string): boolean {
  return isDatetimeDtype(dtype) && typeof value === "number" && !isNaN(value);
}

/**
 * Parses a Python-style numeric format string into its three parts.
 *
 * Python format strings follow the pattern:
 *   <literal prefix>{:<d3-compatible spec>}<literal suffix>
 *
 * Examples:
 *   "$ {:.0f}"  → { prefix: "$ ", spec: ".0f",  suffix: "" }
 *   "{:.2%}"    → { prefix: "",   spec: ".2%",   suffix: "" }
 *   "$ {:.2f}"  → { prefix: "$ ", spec: ".2f",   suffix: "" }
 *   "{:,.0f}"   → { prefix: "",   spec: ",.0f",  suffix: "" }
 *
 * Returns null when the string does not contain a {:...} placeholder.
 *
 * Internals — what String.match() returns for regex /^(.*?)\{:(.+?)\}(.*)$/:
 *   match[0]  full matched string  e.g. "$ {:.0f}"   ← skipped via [, ...]
 *   match[1]  capture group 1      e.g. "$ "          → prefix
 *   match[2]  capture group 2      e.g. ".0f"         → spec  (passed to d3Format)
 *   match[3]  capture group 3      e.g. ""            → suffix
 */
function parsePythonFormatString(
  formatString: string
): { prefix: string; spec: string; suffix: string } | null {
  const match = formatString.match(/^(.*?)\{:(.+?)\}(.*)$/);
  if (!match) return null;
  const [, prefix, spec, suffix] = match;
  return { prefix, spec, suffix };
}

/**
 * Converts a Python strftime pattern to a js-joda DateTimeFormatter pattern.
 * epochConverterWithTimeZone expects js-joda syntax (e.g. "yyyy-MM-dd"),
 * but format_map sends Python strftime syntax (e.g. "%Y-%m-%d").
 */
function strftimeToJodaPattern(strftime: string): string {
  return strftime
    .replace(/%Y/g, "yyyy")
    .replace(/%m/g, "MM")
    .replace(/%d/g, "dd")
    .replace(/%H/g, "HH")
    .replace(/%M/g, "mm")
    .replace(/%S/g, "ss")
    .replace(/%b/g, "MMM")
    .replace(/%B/g, "MMMM")
    .replace(/%A/g, "EEEE")
    .replace(/%a/g, "EEE");
}

/**
 * Renders a PandasDataFrameProto as a formatted table.
 * Uses format_map and column_dtypes for cell-level formatting.
 */
const FormattedPandasTable: React.FC<FormattedPandasTableProps> = ({ df, setNumberOfRows }) => {
  const { columns, rows } = useMemo(
    () => (df?.data_json ? parsePandasDataJson(df.data_json) : { columns: [], rows: [] }),
    [df?.data_json]
  );
  // column name → Python format string
  const formatMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (df?.format_map ?? []).forEach((entry) => {
      if (entry.column_name) {
        map[entry.column_name] = entry.format_string ?? "";
      }
    });
    return map;
  }, [df?.format_map]);

  // column name → pandas dtype string
  const dtypeMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (df?.column_dtypes ?? []).forEach((entry) => {
      if (entry.column_name) {
        map[entry.column_name] = entry.dtype ?? "";
      }
    });
    return map;
  }, [df?.column_dtypes]);

  /**
   * Per-column metadata computed once (not per cell).
   *
   * Moves these expensive operations out of the cell render loop:
   *   - dtype category checks  (isNumericDtype / isDatetimeDtype)
   *   - format string parsing  (parsePythonFormatString — regex)
   *   - strftime conversion    (strftimeToJodaPattern — string replacements)
   *   - d3Format compilation   (d3Format(spec) — parses spec into a formatter fn)
   *
   * The cell render loop only calls the pre-built `formatter(value)` function.
   */
  const columnMeta = useMemo(() => {
    return columns?.map((col, colIndex) => {
      const dtype       = dtypeMap[col];
      const formatString = formatMap[col];
      const isDatetime  = isDatetimeDtype(dtype);
      const align: "left" | "right" = getPandasDtypeCategory(dtype) === PandasDtypeCategory.NUMERIC
          ? "right" : "left";

      // Pre-parse the Python format string and compile the d3 formatter once per column
      const parsedFmt   = formatString ? parsePythonFormatString(formatString) : null;
      const d3Formatter = parsedFmt
        ? (() => { try { return d3Format(parsedFmt.spec); } catch { return null; } })()
        : null;

      // Pre-convert strftime → joda pattern once per datetime column
      const jodaPattern = isDatetime
        ? (formatString ? strftimeToJodaPattern(formatString) : "yyyy-MM-dd")
        : null;

      /** Formats a single cell value using the pre-compiled column metadata. */
      const formatter = (value: any): string => {
        if (value === null || value === undefined) return "—";

        if (isDatetimeCellValue(value, dtype) && jodaPattern) {
          try { return epochConverterWithTimeZone(value, jodaPattern, "UTC"); }
          catch { return String(value); }
        }

        if (isNumericCellValue(value, dtype) && parsedFmt && d3Formatter) {
          try { return `${parsedFmt.prefix}${d3Formatter(Number(value))}${parsedFmt.suffix}`; }
          catch { return `${parsedFmt.prefix}${value}${parsedFmt.suffix}`; }
        }

        return String(value);
      };

      return { col, align, formatter };
    });
  }, [columns, dtypeMap, formatMap]);

  useEffect(() => {
    if (setNumberOfRows) setNumberOfRows(rows?.length ?? 0);
  }, [rows?.length, setNumberOfRows]);

  if (!df || columns?.length === 0) {
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          No data available.
        </Text>
      </Box>
    );
  }

  const tablePad = { base: 1, md: 2 } as const;

  // Exactly 2 columns: layout="fixed" forces an even 50/50 column split across full width.
  if (columns?.length <= 2) {
    return (
      <Box w="100%" overflowX="auto" px={tablePad} justifyContent="center">
        <Table size="sm" variant="simple" w="100%" layout="auto" align="center">
          <Thead bg="gray.50" position="sticky" top={0} zIndex={10} boxShadow="sm">
            <Tr>
              {columnMeta?.map(({ col, align }, colIndex) => (
                <Th
                  key={`${col}-${colIndex}`}
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.900"
                  textTransform="none"
                  letterSpacing="normal"
                  borderColor="gray.200"
                  px={3}
                  py={2}
                  w="50%"
                  whiteSpace="nowrap"
                  textAlign={align}
                >
                  {col}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows?.map((row, i) => (
              <Tr key={i} _hover={{ bg: "gray.50" }} transition="background 0.1s">
                {columnMeta?.map(({ col, align, formatter }, colIndex) => (
                  <Td
                    key={`${col}-${i}-${colIndex}`}
                    fontSize="0.8rem"
                    color="gray.700"
                    borderColor="gray.100"
                    px={3}
                    py={1.5}
                    w="50%"
                    whiteSpace="nowrap"
                    fontWeight="600"
                    textAlign={align}
                  >
                    {formatter(row?.[col])}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  }

  return (
    <Box w="100%" overflowX="auto" px={tablePad}>
      <Table size="sm" variant="simple" w="100%">
        <Thead bg="gray.50" position="sticky" top={0} zIndex={10} boxShadow="sm">
          <Tr>
            {columnMeta?.map(({ col, align }, colIndex) => (
              <Th
                key={`${col}-${colIndex}`}
                fontSize="xs"
                fontWeight="700"
                color="gray.900"
                textTransform="none"
                letterSpacing="normal"
                borderColor="gray.200"
                px={3}
                py={2}
                whiteSpace="nowrap"
                textAlign={align}
              >
                {col}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows?.map((row, i) => (
            <Tr key={i} _hover={{ bg: "gray.50" }} transition="background 0.1s">
              {columnMeta?.map(({ col, align, formatter }, colIndex) => (
                <Td
                  key={`${col}-${i}-${colIndex}`}
                  fontSize="0.8rem"
                  color="gray.700"
                  borderColor="gray.100"
                  px={3}
                  py={1.5}
                  whiteSpace="nowrap"
                  fontWeight="600"
                  textAlign={align}
                >
                  {formatter(row?.[col])}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default FormattedPandasTable;
